/**
 * Veluna Product Side Cart - Arrae-style subscription selector
 * Opens when "Add to Cart" is clicked, allowing users to choose purchase type and subscription frequency
 */

(function() {
  'use strict';

  // Loader flag: lets other scripts detect whether this file loaded and parsed successfully.
  // This is critical when some visitors still have cached HTML pointing to an older broken build.
  window.__VELUNA_SIDE_CART_LOADED = true;

  const boundKeys = new WeakMap();
  const productJsonCache = new Map();

  function bindOnce(element, key, eventName, handler, options) {
    if (!element) return;
    let keys = boundKeys.get(element);
    if (!keys) {
      keys = new Set();
      boundKeys.set(element, keys);
    }
    if (keys.has(key)) return;
    keys.add(key);
    element.addEventListener(eventName, handler, options);
  }

  // Initialize side cart system (idempotent via bindOnce)
  function initProductSideCart() {
    // HARDENED: Capture-phase delegated click handler for all side-cart CTAs.
    // Why: On some pages (notably Ritual Essentials), other scripts can attach click handlers
    // that prevent the bubble-phase handler from firing. Capture-phase delegation ensures
    // `openSideCart()` always wins when the DOM hooks are present.
    // Use `window` as the target so this runs *before* any `document` capture listeners.
    bindOnce(window, 'open-side-cart-capture', 'click', function(e) {
      try {
        const target = e && e.target;
        if (!target || typeof target.closest !== 'function') return;

        const button = target.closest('[data-open-side-cart]');
        if (!button) return;
        // Never treat clicks inside the drawer itself as open-intent.
        if (button.closest('.veluna-product-side-cart')) return;

        const productId = button.getAttribute('data-product-id');
        const variantId = button.getAttribute('data-variant-id');
        if (!productId) return;

        // Only intercept if the side cart exists; otherwise allow other handlers.
        const sideCartExists = !!document.getElementById(`veluna-product-side-cart-${productId}`);
        if (!sideCartExists) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Pricing continuity (2026-09-09): a card that displays a one-time price passes
        // data-side-cart-initial="onetime" so the drawer opens on that exact configuration.
        openSideCart(productId, variantId, {
          initial: button.getAttribute('data-side-cart-initial') || '',
          cadence: button.getAttribute('data-side-cart-cadence') || ''
        });
      } catch (_) {
        // Fail open (do not block clicks) if anything unexpected happens.
      }
    }, { capture: true });

    // Store original button HTML for all side cart buttons
    const sideCartButtons = document.querySelectorAll('[data-side-cart-add-to-cart]');
    sideCartButtons.forEach((button) => {
      if (!button.getAttribute('data-original-html')) {
        button.setAttribute('data-original-html', button.innerHTML);
      }
    });

    // Find all "Add to Cart" buttons that should open side cart
    const addToCartButtons = document.querySelectorAll('[data-open-side-cart]');
    addToCartButtons.forEach((button) => {
      bindOnce(button, 'open-side-cart', 'click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const productId = this.getAttribute('data-product-id');
        const variantId = this.getAttribute('data-variant-id');
        if (productId) {
          openSideCart(productId, variantId);
        }
      });
    });

    // Close side cart handlers
    const closeButtons = document.querySelectorAll('[data-side-cart-close]');
    closeButtons.forEach((button) => {
      bindOnce(button, 'close-side-cart', 'click', closeSideCart);
    });

    // Purchase type selection
    const purchaseTypeRadios = document.querySelectorAll('.veluna-product-side-cart__radio[name^="purchase-type"]');
    purchaseTypeRadios.forEach((radio) => {
      bindOnce(radio, 'purchase-type-change', 'change', function(e) {
        e.preventDefault();
        e.stopPropagation();
        updatePurchaseType(this.value);
      });
    });

    // Schedule selection
    const scheduleRadios = document.querySelectorAll('.veluna-product-side-cart__radio[name^="schedule"]');
    scheduleRadios.forEach((radio) => {
      bindOnce(radio, 'schedule-change', 'change', function(e) {
        e.preventDefault();
        e.stopPropagation();
        updateSchedule(this.value);
      });
    });

    // Servings selection (for one-time purchase)
    const servingsRadios = document.querySelectorAll('.veluna-product-side-cart__radio[name^="servings"]');
    servingsRadios.forEach((radio) => {
      bindOnce(radio, 'servings-change', 'change', function(e) {
        e.preventDefault();
        e.stopPropagation();
        const selectedOption = this.closest('[data-servings]');
        if (selectedOption) {
          selectServingsOption(selectedOption);
          updatePrice(this.closest('.veluna-product-side-cart'));
        }
      });
    });

    // Why Subscribe toggle
    const whySubscribeToggles = document.querySelectorAll('[data-why-subscribe-toggle]');
    whySubscribeToggles.forEach((toggle) => {
      bindOnce(toggle, 'why-subscribe-toggle', 'click', function() {
        const container = this.closest('[data-why-subscribe]');
        const isExpanded = container.getAttribute('data-expanded') === 'true';
        container.setAttribute('data-expanded', !isExpanded);
      });
    });

    // Add to Cart from side cart
    const sideCartAddToCartButtons = document.querySelectorAll('[data-side-cart-add-to-cart]');
    sideCartAddToCartButtons.forEach((button) => {
      bindOnce(button, 'side-cart-add-to-cart', 'click', function() {
        handleSideCartAddToCart(this);
      });
    });

    // Close on backdrop click
    bindOnce(document, 'backdrop-close', 'click', function(e) {
      if (e.target && e.target.classList && e.target.classList.contains('veluna-product-side-cart__backdrop')) {
        closeSideCart();
      }
    });

    // Close on Escape key
    bindOnce(document, 'escape-close', 'keydown', function(e) {
      if (e.key === 'Escape') {
        const sideCart = document.querySelector('.veluna-product-side-cart[aria-hidden="false"]');
        if (sideCart) {
          closeSideCart();
        }
      }
    });
  }
  
  function openSideCart(productId, variantId, opts) {
    opts = opts || {};
    // Find or create side cart for this product
    let sideCart = document.getElementById(`veluna-product-side-cart-${productId}`);
    
    if (!sideCart) {
      // Side cart doesn't exist yet - we need to render it
      // For now, we'll assume it's already in the DOM (rendered via Liquid)
      console.warn('[VELUNA SIDE CART] Side cart not found for product:', productId);
      return;
    }
    
    // CRITICAL: Reset button state before showing side cart
    const addToCartButton = sideCart.querySelector('[data-side-cart-add-to-cart]');
    if (addToCartButton) {
      // Restore original button HTML from data attribute or default
      const originalHTML = addToCartButton.getAttribute('data-original-html');
      if (originalHTML) {
        addToCartButton.innerHTML = originalHTML;
      } else {
        // Fallback: restore from template structure
        const priceElement = addToCartButton.querySelector('[data-side-cart-price]');
        const priceText = priceElement ? priceElement.textContent : '';
        addToCartButton.innerHTML = '<span class="veluna-product-side-cart__add-to-cart-text">Add to Cart</span>' + 
          (priceText ? '<span class="veluna-product-side-cart__add-to-cart-price" data-side-cart-price>' + priceText + '</span>' : '');
      }
      addToCartButton.disabled = false;
      // Store original HTML for future resets
      if (!addToCartButton.getAttribute('data-original-html')) {
        addToCartButton.setAttribute('data-original-html', addToCartButton.innerHTML);
      }
    }
    
    // Show side cart - override any inline `... !important` hidden styles deterministically
    sideCart.setAttribute('aria-hidden', 'false');
    try {
      sideCart.style.setProperty('display', 'block', 'important');
      sideCart.style.setProperty('visibility', 'visible', 'important');
      sideCart.style.setProperty('opacity', '1', 'important');
      sideCart.style.setProperty('pointer-events', 'auto', 'important');
    } catch (e) {
      sideCart.style.display = 'block';
      sideCart.style.visibility = 'visible';
      sideCart.style.opacity = '1';
      sideCart.style.pointerEvents = 'auto';
    }
    document.body.style.overflow = 'hidden';
    
    // Set initial state
    const subscribeOption = sideCart.querySelector('[data-purchase-type="subscribe"]');
    const onetimeOption = sideCart.querySelector('[data-purchase-type="onetime"]');
    const scheduleSection = sideCart.querySelector('[data-schedule-section]');
    
    // Default to Subscribe & Save
    if (subscribeOption) {
      subscribeOption.classList.add('active');
      subscribeOption.querySelector('input[type="radio"]').checked = true;
    }
    if (onetimeOption) {
      onetimeOption.classList.remove('active');
      onetimeOption.querySelector('input[type="radio"]').checked = false;
    }
    if (scheduleSection) {
      scheduleSection.style.display = 'block';
    }
    const onetimeSection = sideCart.querySelector('[data-onetime-section]');
    if (onetimeSection) {
      onetimeSection.style.display = 'none';
    }
    
    // Select default schedule (6-month - BEST DEAL)
    const defaultSchedule = sideCart.querySelector('[data-schedule="6"]') || sideCart.querySelector('[data-schedule]');
    if (defaultSchedule) {
      selectScheduleOption(defaultSchedule);
    }

    // Ensure side cart and footer button use the variant tied to the active schedule.
    syncVariantFromActiveSchedule(sideCart);

    // Pricing continuity: when opened from a card whose primary price is the one-time
    // price of a specific variant, start on ONE-TIME with the matching unit count, and
    // pre-select the matching subscription cadence so switching to Subscribe shows the
    // same cadence the card quoted. The cadence is resolved from the drawer's own
    // schedule options (data-variant-id per cadence), never hardcoded.
    sideCart.removeAttribute('data-origin-cadence');
    if (opts.initial === 'onetime' || opts.initial === 'subscribe') {
      const originMonths = resolveCadenceMonthsForVariant(sideCart, variantId);
      sideCart.setAttribute('data-origin-cadence', String(originMonths));
      // Subscription default = the cadence the card advertised (data-side-cart-cadence), which the
      // card resolved from Shopify as the longest genuine cadence; otherwise the longest schedule
      // option actually rendered (each option exists only when a real allocation exists).
      let wantMonths = parseInt(opts.cadence || '', 10);
      let originSchedule = Number.isFinite(wantMonths) && wantMonths > 0
        ? sideCart.querySelector(`[data-schedule="${wantMonths}"]`)
        : null;
      if (!originSchedule) {
        let best = null, bestMonths = 0;
        sideCart.querySelectorAll('[data-schedule]').forEach((o) => {
          const m = parseInt(o.getAttribute('data-schedule'), 10);
          if (Number.isFinite(m) && m > bestMonths) { bestMonths = m; best = o; }
        });
        originSchedule = best || sideCart.querySelector(`[data-schedule="${originMonths}"]`);
      }
      if (originSchedule) selectScheduleOption(originSchedule);
      // "subscribe": open on Subscribe & Save with that cadence selected;
      // "onetime": open on One Time Purchase with the matching unit count (origin cadence, 1 unit).
      try { updatePurchaseType(opts.initial); } catch (e) {}
    }

    // Open "Why Subscribe?" by default
    const whySubscribe = sideCart.querySelector('[data-why-subscribe]');
    if (whySubscribe) {
      whySubscribe.setAttribute('data-expanded', 'true');
    }
    
    // Update price (this will also update the button price)
    updatePrice(sideCart);

    // Hydrate subscription UI from Shopify product.js (fixes hard-coded %/prices)
    const handle = sideCart.getAttribute('data-product-handle');
    if (handle) {
      fetchProductJsonByHandle(handle)
        .then((productJson) => {
          if (!productJson) return;
          hydrateSubscribeAndSaveUi(sideCart, productJson);
          updatePrice(sideCart);
        })
        .catch(() => {});
    }
  }

  function resolveCadenceMonthsForVariant(sideCart, variantId) {
    const target = variantId != null ? String(variantId) : '';
    if (sideCart && target) {
      // The base (single-unit) variant always means the 1-unit / 1-month configuration.
      // Products whose variants are not cadence-named (e.g. "1 Jar / 3 Jars / 6 Jars")
      // map every schedule option to the same variant, so check the base first and
      // otherwise take the SMALLEST matching cadence.
      const baseId = getBaseVariantId(sideCart);
      if (baseId && String(baseId) === target) return 1;
      let best = null;
      const options = sideCart.querySelectorAll('[data-schedule][data-variant-id]');
      for (let i = 0; i < options.length; i++) {
        if (String(options[i].getAttribute('data-variant-id')) === target) {
          const n = parseInt(options[i].getAttribute('data-schedule'), 10);
          if (Number.isFinite(n) && n > 0 && (best === null || n < best)) best = n;
        }
      }
      if (best !== null) return best;
    }
    return 1;
  }

  function syncVariantFromScheduleOption(sideCart, option) {
    if (!sideCart || !option) return;
    const variantIdRaw = option.getAttribute('data-variant-id');
    if (!variantIdRaw) return;
    const variantId = parseInt(String(variantIdRaw), 10);
    if (!variantId || Number.isNaN(variantId)) return;

    sideCart.setAttribute('data-variant-id', String(variantId));

    const addBtn = sideCart.querySelector('[data-side-cart-add-to-cart]');
    if (addBtn) addBtn.setAttribute('data-variant-id', String(variantId));
  }

  function syncVariantFromActiveSchedule(sideCart) {
    if (!sideCart) return;
    const active = sideCart.querySelector('[data-schedule].active');
    if (active) syncVariantFromScheduleOption(sideCart, active);
  }

  function syncVariantToBaseVariant(sideCart) {
    if (!sideCart) return;
    const baseRaw = sideCart.getAttribute('data-base-variant-id');
    if (!baseRaw) return;
    const baseId = parseInt(String(baseRaw), 10);
    if (!baseId || Number.isNaN(baseId)) return;
    sideCart.setAttribute('data-variant-id', String(baseId));
    const addBtn = sideCart.querySelector('[data-side-cart-add-to-cart]');
    if (addBtn) addBtn.setAttribute('data-variant-id', String(baseId));
  }

  function getBaseVariantId(sideCart) {
    if (!sideCart) return null;
    const baseRaw = sideCart.getAttribute('data-base-variant-id');
    if (!baseRaw) return null;
    const baseId = parseInt(String(baseRaw), 10);
    if (!baseId || Number.isNaN(baseId)) return null;
    return baseId;
  }
  
  function closeSideCart() {
    const sideCarts = document.querySelectorAll('.veluna-product-side-cart[aria-hidden="false"]');
    sideCarts.forEach(sideCart => {
      sideCart.setAttribute('aria-hidden', 'true');
      // Restore inline hidden styles
      try {
        sideCart.style.setProperty('display', 'none', 'important');
        sideCart.style.setProperty('visibility', 'hidden', 'important');
        sideCart.style.setProperty('opacity', '0', 'important');
        sideCart.style.setProperty('pointer-events', 'none', 'important');
      } catch (e) {
        sideCart.style.display = 'none';
        sideCart.style.visibility = 'hidden';
        sideCart.style.opacity = '0';
        sideCart.style.pointerEvents = 'none';
      }
    });
    
    // Restore body scroll on ALL screen sizes (drawer open will re-lock it)
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  }
  
  function updatePurchaseType(type) {
    const sideCart = document.querySelector('.veluna-product-side-cart[aria-hidden="false"]');
    if (!sideCart) return;
    
    // Store current scroll position to prevent jumping
    const content = sideCart.querySelector('.veluna-product-side-cart__content');
    const scrollTop = content ? content.scrollTop : 0;
    
    const subscribeOption = sideCart.querySelector('[data-purchase-type="subscribe"]');
    const onetimeOption = sideCart.querySelector('[data-purchase-type="onetime"]');
    const scheduleSection = sideCart.querySelector('[data-schedule-section]');
    const onetimeSection = sideCart.querySelector('[data-onetime-section]');
    
    if (type === 'subscribe') {
      if (subscribeOption) {
        subscribeOption.classList.add('active');
        subscribeOption.querySelector('input[type="radio"]').checked = true;
      }
      if (onetimeOption) {
        onetimeOption.classList.remove('active');
        onetimeOption.querySelector('input[type="radio"]').checked = false;
      }
      if (scheduleSection) {
        scheduleSection.style.display = 'block';
      }
      if (onetimeSection) {
        onetimeSection.style.display = 'none';
      }

      // IMPORTANT: For Supliful fulfillment, subscriptions should use the base SKU
      // and multiply quantity by cadence (1/3/6). Do NOT switch to pack variants.
      syncVariantToBaseVariant(sideCart);
    } else {
      if (subscribeOption) {
        subscribeOption.classList.remove('active');
        subscribeOption.querySelector('input[type="radio"]').checked = false;
      }
      if (onetimeOption) {
        onetimeOption.classList.add('active');
        onetimeOption.querySelector('input[type="radio"]').checked = true;
      }
      if (scheduleSection) {
        scheduleSection.style.display = 'none';
      }
      if (onetimeSection) {
        onetimeSection.style.display = 'block';
        // Select default option (3 jars - BEST DEAL)
        const originCadence = sideCart.getAttribute('data-origin-cadence');
        const defaultServings = (originCadence && onetimeSection.querySelector(`[data-servings="${originCadence}"]`))
          || onetimeSection.querySelector('[data-servings="3"]');
        if (defaultServings) {
          selectServingsOption(defaultServings);
        }
      }

      // One-time purchase uses quantity-based servings; ensure we're on the single-unit/base variant.
      syncVariantToBaseVariant(sideCart);
    }
    
    updatePrice(sideCart);
    
    // Restore scroll position after DOM updates
    if (content) {
      requestAnimationFrame(() => {
        content.scrollTop = scrollTop;
      });
    }
  }
  
  function selectServingsOption(option) {
    const sideCart = option.closest('.veluna-product-side-cart');
    if (!sideCart) return;
    
    // Remove active from all options
    const allOptions = sideCart.querySelectorAll('[data-servings]');
    allOptions.forEach(opt => opt.classList.remove('active'));
    
    // Add active to selected
    option.classList.add('active');
    option.querySelector('input[type="radio"]').checked = true;
  }
  
  function updateSchedule(value) {
    const sideCart = document.querySelector('.veluna-product-side-cart[aria-hidden="false"]');
    if (!sideCart) return;
    
    const selectedOption = sideCart.querySelector(`[data-schedule="${value}"]`);
    if (selectedOption) {
      selectScheduleOption(selectedOption);
      updatePrice(sideCart);
    }
  }
  
  function selectScheduleOption(option) {
    const sideCart = option.closest('.veluna-product-side-cart');
    if (!sideCart) return;
    
    // Remove active from all options
    const allOptions = sideCart.querySelectorAll('.veluna-product-side-cart__schedule-option');
    allOptions.forEach(opt => opt.classList.remove('active'));
    
    // Add active to selected
    option.classList.add('active');
    option.querySelector('input[type="radio"]').checked = true;

    // For Supliful fulfillment, keep base variant; cadence is represented by quantity.
    syncVariantToBaseVariant(sideCart);
  }
  
  function updatePrice(sideCart) {
    const purchaseType = sideCart.querySelector('input[name^="purchase-type"]:checked')?.value;
    const scheduleOption = sideCart.querySelector('[data-schedule].active');
    const servingsOption = sideCart.querySelector('[data-servings].active');
    const priceElement = sideCart.querySelector('[data-side-cart-price]');
    
    if (purchaseType === 'subscribe' && scheduleOption) {
      const billedLabel = scheduleOption.getAttribute('data-billed-label');
      const price = scheduleOption.getAttribute('data-price');
      const formattedPrice = formatMoney(parseFloat(price) * 100); // Convert to cents
      if (priceElement) {
        priceElement.textContent = billedLabel ? ` | ${billedLabel}` : ` | ${formattedPrice}/month`;
      }
    } else if (purchaseType === 'onetime' && servingsOption) {
      // One-time purchase with servings
      const price = servingsOption.getAttribute('data-price');
      const formattedPrice = formatMoney(parseFloat(price) * 100);
      if (priceElement) {
        priceElement.textContent = ` | ${formattedPrice}`;
      }
    } else {
      // Fallback to original price
      const originalPrice = sideCart.querySelector('[data-schedule]')?.getAttribute('data-original-price') ||
                          sideCart.querySelector('[data-servings]')?.getAttribute('data-original-price');
      if (originalPrice && priceElement) {
        const formattedPrice = formatMoney(parseFloat(originalPrice) * 100);
        priceElement.textContent = ` | ${formattedPrice}`;
      }
    }
  }
  
  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  function parseCadenceMonthsFromPlan(plan) {
    if (!plan) return null;

    const candidates = [];
    if (typeof plan.name === 'string') candidates.push(plan.name);

    const options = Array.isArray(plan.options) ? plan.options : [];
    for (const opt of options) {
      if (!opt) continue;
      if (typeof opt.value === 'string') candidates.push(opt.value);
      if (typeof opt.name === 'string') candidates.push(opt.name);
    }

    for (const text of candidates) {
      const m = String(text).match(/\b(\d+)\s*month\b/i);
      if (m) {
        const n = Number(m[1]);
        if (Number.isFinite(n) && n > 0) return n;
      }
    }

    return null;
  }

  function getPercentDiscountFromPlan(plan) {
    const adjustments = plan && Array.isArray(plan.price_adjustments) ? plan.price_adjustments : [];
    for (const adj of adjustments) {
      if (!adj) continue;
      if (adj.value_type === 'percentage' && typeof adj.value === 'number' && adj.value > 0) {
        return Math.round(adj.value);
      }
    }
    return null;
  }

  function findVariantInProductJson(product, variantId) {
    if (!product || variantId == null) return null;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const target = String(variantId);
    return variants.find((v) => v && String(v.id) === target) || null;
  }

  function findAllocationForPlan(variant, planId) {
    if (!variant || planId == null) return null;
    const allocs = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations : [];
    const target = String(planId);
    return allocs.find((a) => a && a.selling_plan_id != null && String(a.selling_plan_id) === target) || null;
  }

  function hydrateSubscribeAndSaveUi(sideCart, productJson) {
    if (!sideCart) return;

    const productHandle = sideCart.getAttribute('data-product-handle') || '';
    const baseVariantId = getBaseVariantId(sideCart) || sideCart.getAttribute('data-variant-id');
    const bundleLike = isBundleSetHandle(productHandle);

    const subscribeOption = sideCart.querySelector('[data-purchase-type="subscribe"]');
    const scheduleSection = sideCart.querySelector('[data-schedule-section]');

    // Bundle parent products now have their own selling plans in Shopify, so we
    // read selling_plan_groups directly from the parent product JSON for BOTH
    // bundles and individual products. The component decomposition is only used
    // at ATC time, not for price display.
    const groups = (productJson && Array.isArray(productJson.selling_plan_groups))
      ? productJson.selling_plan_groups
      : [];

    // IMPORTANT: Some products can have multiple selling plan groups; don't assume index 0.
    const plans = [];
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const groupPlans = group && Array.isArray(group.selling_plans) ? group.selling_plans : [];
      for (let j = 0; j < groupPlans.length; j++) {
        const p = groupPlans[j];
        if (p && p.id != null) plans.push(p);
      }
    }

    const hasSellingPlans = plans.length > 0;

    if (!hasSellingPlans && !bundleLike) {
      if (subscribeOption) subscribeOption.style.display = 'none';
      if (scheduleSection) scheduleSection.style.display = 'none';
      try { updatePurchaseType('onetime'); } catch (e) {}
      return;
    }

    // Even if a bundle parent has no selling plans (edge case), keep the subscribe
    // option visible since the ATC path handles component decomposition.
    if (!hasSellingPlans && bundleLike) {
      // Leave Liquid-rendered prices as-is; nothing to hydrate.
      return;
    }

    // Build plan lookup by cadence months.
    const planByMonths = new Map();
    let maxPercent = 0;
    for (const plan of plans) {
      if (!plan || plan.id == null) continue;
      const cadenceMonths = parseCadenceMonthsFromPlan(plan);
      if (!cadenceMonths) continue;

      const percentOff = getPercentDiscountFromPlan(plan);
      if (typeof percentOff === 'number' && percentOff > maxPercent) maxPercent = percentOff;

      const existing = planByMonths.get(cadenceMonths);
      if (!existing || (typeof percentOff === 'number' && typeof existing.percentOff === 'number' && percentOff > existing.percentOff)) {
        planByMonths.set(cadenceMonths, { planId: plan.id, cadenceMonths, percentOff });
      }
    }

    if (subscribeOption) {
      const badge = subscribeOption.querySelector('.veluna-product-side-cart__purchase-badge');
      if (badge && maxPercent > 0) {
        badge.textContent = `${maxPercent}% OFF`;
      }
    }

    // NOTE: Bundle pricing now uses the bundle parent's selling plan allocations
    // (same code path as individual products below). Component decomposition only
    // happens at ATC time, not for price display.

    const unitSingular = (sideCart.getAttribute('data-unit-singular') || 'Item').toLowerCase();
    const unitPlural = (sideCart.getAttribute('data-unit-plural') || 'Items').toLowerCase();
    const scheduleOptions = sideCart.querySelectorAll('.veluna-product-side-cart__schedule-option[data-schedule]');
    scheduleOptions.forEach((opt) => {
      // Options carrying data-selling-plan-id are rendered from the cadence variant's own
      // selling-plan allocation in Liquid; that is the source of truth, do not rewrite it.
      if (opt.hasAttribute('data-selling-plan-id')) return;
      const months = Number(opt.getAttribute('data-schedule'));
      if (!Number.isFinite(months) || months <= 0) return;

      const info = planByMonths.get(months);
      if (!info || info.planId == null) return;

      // IMPORTANT: To ensure fulfillment is sourced from the base Supliful SKU,
      // pricing is derived from the base variant allocation; cadence is represented by quantity.
      const variantForOption = findVariantInProductJson(productJson, baseVariantId);
      if (!variantForOption) return;

      // With base SKU + quantity=months, the per-month *original* is just the base unit price.
      const basePerMonthCents = (typeof variantForOption.price === 'number')
        ? Math.round(variantForOption.price)
        : null;

      const alloc = findAllocationForPlan(variantForOption, info.planId);

      // Allocation prices from /products/{handle}.js are per-variant (per unit). Since we will add
      // quantity=months, the per-month price equals the per-unit discounted price.
      const perUnitDiscountedCents = alloc && typeof alloc.per_delivery_price === 'number'
        ? alloc.per_delivery_price
        : alloc && typeof alloc.price === 'number'
          ? alloc.price
          : (typeof variantForOption.price === 'number' && typeof info.percentOff === 'number')
            ? Math.round(variantForOption.price * (1 - (info.percentOff / 100)))
            : null;
      if (typeof perUnitDiscountedCents !== 'number') return;

      const perMonthCents = Math.round(perUnitDiscountedCents);
      opt.setAttribute('data-price', (perMonthCents / 100).toFixed(2));

      const priceEl = opt.querySelector('.veluna-product-side-cart__schedule-price');
      if (priceEl) priceEl.textContent = `${formatMoney(perMonthCents)}/month`;

      const originalEl = opt.querySelector('.veluna-product-side-cart__schedule-original');
      if (originalEl && typeof basePerMonthCents === 'number') originalEl.textContent = formatMoney(basePerMonthCents);

      const detailsEl = opt.querySelector('.veluna-product-side-cart__schedule-details');
      if (detailsEl) {
        const label = months === 1 ? 'month' : 'months';
        const units = months === 1 ? unitSingular : unitPlural;
        detailsEl.textContent = `${months} ${units}, delivered every ${months} ${label}`;
      }
    });

    if (window.VELUNA_ATC_DEBUG || window.VELUNA_DEBUG) {
      console.log('[VELUNA SIDE CART] Hydrated subscribe UI', {
        productHandle,
        variantId: baseVariantId,
        maxPercent,
        cadenceMonths: Array.from(planByMonths.keys()).sort((a, b) => a - b)
      });
    }
  }

  function isBundleSetHandle(productHandle) {
    if (!productHandle) return false;
    const s = String(productHandle).toLowerCase();
    return s.indexOf('ritual-set') !== -1
      || s.indexOf('starter-set') !== -1 || s.indexOf('starter-pack') !== -1 || s.indexOf('stater-set') !== -1
      || s.indexOf('booster-set') !== -1 || s.indexOf('booster-pack') !== -1;
  }

  // Map bundle ritual/tier to component product handles.
  // These are the actual shippable products (Supliful SKUs) that make up each set.
  const BUNDLE_COMPONENT_HANDLES = {
    circadian_reset: {
      starter: ['magnesium-glycinate', 'sleep-formula'],
      booster: ['ayurvedic-complex', 'sleep-formula'],
      full: ['ayurvedic-complex', 'magnesium-glycinate', 'sleep-formula', 'sleep-strips']
    },
    mind_calm: {
      starter: ['ashwagandha', 'magnesium-glycinate'],
      booster: ['sleep-formula', 'sleep-strips'],
      full: ['ashwagandha', 'magnesium-glycinate', 'sleep-formula', 'sleep-strips']
    },
    deep_restore: {
      starter: ['magnesium-glycinate', 'vs1-recovery-cream'],
      booster: ['sleep-formula', 'grass-fed-collagen-peptides-powder-chocolate'],
      full: ['magnesium-glycinate', 'sleep-formula', 'grass-fed-collagen-peptides-powder-chocolate', 'vs1-recovery-cream']
    },
    sacred_soothe: {
      starter: ['hyaluronic-acid-serum', 'sleep-plus-collagen-cream'],
      booster: ['ashwagandha', 'grass-fed-collagen-peptides-powder-chocolate'],
      full: ['ashwagandha', 'sleep-formula', 'grass-fed-collagen-peptides-powder-chocolate', 'hyaluronic-acid-serum']
    }
  };

  function getBundleMetaFromSideCart(sideCart) {
    if (!sideCart) return { ritualType: '', ritualTier: '' };
    const ritualTypeRaw = (sideCart.getAttribute('data-ritual-type') || '').trim();
    const ritualTierRaw = (sideCart.getAttribute('data-ritual-tier') || '').trim();

    if (ritualTypeRaw && ritualTierRaw) {
      return { ritualType: ritualTypeRaw, ritualTier: ritualTierRaw };
    }

    const handle = (sideCart.getAttribute('data-product-handle') || '').trim();
    const derived = deriveBundleMetaFromHandle(handle);
    return {
      ritualType: ritualTypeRaw || derived.ritualType || '',
      ritualTier: ritualTierRaw || derived.ritualTier || ''
    };
  }

  function deriveBundleMetaFromHandle(productHandle) {
    const h = (productHandle || '').toString().toLowerCase();
    if (!h) return { ritualType: '', ritualTier: '' };

    // Tier
    let ritualTier = '';
    if (h.indexOf('starter-set') !== -1 || h.indexOf('starter-pack') !== -1 || h.indexOf('stater-set') !== -1) ritualTier = 'starter';
    else if (h.indexOf('booster-set') !== -1 || h.indexOf('booster-pack') !== -1) ritualTier = 'booster';
    else if (h.indexOf('ritual-set') !== -1) ritualTier = 'full';

    // Type
    let ritualType = '';
    if (h.indexOf('circadian-reset') !== -1) ritualType = 'circadian_reset';
    else if (h.indexOf('mind-calm') !== -1) ritualType = 'mind_calm';
    else if (h.indexOf('deep-restore') !== -1) ritualType = 'deep_restore';
    else if (h.indexOf('sacred-soothe') !== -1) ritualType = 'sacred_soothe';

    return { ritualType, ritualTier };
  }

  function getComponentHandlesForBundle(ritualType, ritualTier) {
    const type = (ritualType || '').trim().toLowerCase();
    const tier = (ritualTier || '').trim().toLowerCase();
    if (!type || !tier) return null;
    const byTier = BUNDLE_COMPONENT_HANDLES[type];
    if (!byTier) return null;
    return byTier[tier] || null;
  }

  function findBaseVariantIdFromProductJson(productJson) {
    const variants = productJson && Array.isArray(productJson.variants) ? productJson.variants : [];
    if (!variants.length) return null;

    // Prefer 1 Month Delivery / 1 Jar.
    for (const v of variants) {
      const title = (v && v.title ? String(v.title) : '').toLowerCase().trim();
      if (title.indexOf('1 month delivery') !== -1 || title === '1 jar') {
        return v.id;
      }
    }

    // Fallback first.
    return variants[0].id;
  }

  function fetchProductJsonByHandle(productHandle) {
    if (!productHandle) return Promise.resolve(null);
    if (productJsonCache.has(productHandle)) {
      return productJsonCache.get(productHandle);
    }

    const promise = fetch(`/products/${productHandle}.js`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch product JSON (${response.status})`);
        }
        return response.json();
      })
      .catch((error) => {
        console.warn('[VELUNA SIDE CART] Failed to load product JSON:', productHandle, error);
        return null;
      });

    productJsonCache.set(productHandle, promise);
    return promise;
  }

  function findSellingPlanIdForCadence(product, variantId, cadenceMonths) {
    if (!product || !variantId) return null;
    const normalizedVariantId = String(variantId);
    const cadence = cadenceMonths ? String(cadenceMonths) : '';

    const groups = Array.isArray(product.selling_plan_groups) ? product.selling_plan_groups : [];
    if (groups.length === 0) return null;

    // Build plan map for lookup.
    const planById = new Map();
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const plans = Array.isArray(group.selling_plans) ? group.selling_plans : [];
      for (let j = 0; j < plans.length; j++) {
        const plan = plans[j];
        if (plan && plan.id != null) {
          planById.set(String(plan.id), plan);
        }
      }
    }

    // Prefer plans explicitly allocated to this variant.
    let candidatePlanIds = [];
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const variant = variants.find((v) => String(v.id) === normalizedVariantId);
    if (variant && Array.isArray(variant.selling_plan_allocations)) {
      candidatePlanIds = variant.selling_plan_allocations
        .map((a) => (a && a.selling_plan_id != null ? String(a.selling_plan_id) : null))
        .filter(Boolean);
    }

    if (candidatePlanIds.length === 0) {
      candidatePlanIds = Array.from(planById.keys());
    }

    const cadenceRegex = cadence ? new RegExp(`\\b${cadence}\\b`) : null;
    const monthRegex = /month/i;

    let bestId = null;
    let bestScore = -1;

    for (let i = 0; i < candidatePlanIds.length; i++) {
      const planId = candidatePlanIds[i];
      const plan = planById.get(planId);
      if (!plan) continue;

      const name = (plan.name || '').toString();
      const options = Array.isArray(plan.options) ? plan.options : [];
      const optionText = options
        .map((o) => `${o && o.name ? o.name : ''} ${o && o.value ? o.value : ''}`.trim())
        .join(' | ');

      const haystack = `${name} | ${optionText}`.toLowerCase();

      let score = 0;
      if (cadenceRegex && cadenceRegex.test(haystack)) score += 10;
      if (monthRegex.test(haystack)) score += 3;
      // If cadence is not provided, any plan is acceptable.
      if (!cadence) score += 1;

      if (score > bestScore) {
        bestScore = score;
        bestId = planId;
      }
    }

    return bestId;
  }

  function addItemsToCart(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(async (response) => {
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      if (!response.ok) {
        const message = (data && (data.description || data.message))
          ? (data.description || data.message)
          : `Add to cart failed (${response.status})`;
        const error = new Error(message);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      if (data && data.status && data.status >= 400) {
        const error = new Error(data.description || 'Failed to add item to cart');
        error.status = data.status;
        error.data = data;
        throw error;
      }

      return data;
    });
  }

  function getReadableErrorMessage(error) {
    if (!error) return '';

    if (typeof error === 'string') return error;

    const direct = error.description || error.message;
    if (direct) return String(direct);

    const nested = error.data && (error.data.description || error.data.message);
    if (nested) return String(nested);

    try {
      return JSON.stringify(error);
    } catch (e) {
      return '';
    }
  }

  function openCartDrawerReliably() {
    const getDrawerEl = () => {
      return document.querySelector('[data-cart-drawer], [data-veluna-cart-drawer], .veluna-cart-drawer, #CartDrawer, .cart-drawer');
    };
    const isDrawerOpen = () => {
      const el = getDrawerEl();
      if (!el) return false;
      return el.classList.contains('veluna-cart-drawer--open') || el.classList.contains('is-open') || el.getAttribute('aria-hidden') === 'false';
    };

    /** Nuclear reset: remove ALL inline styles that any close handler may have set. */
    const resetDrawerStyles = () => {
      const drawer = getDrawerEl();
      if (!drawer) return;
      // Clear root
      drawer.style.cssText = '';
      // Clear panel
      const panel = drawer.querySelector('.veluna-cart-drawer__panel, [data-cart-drawer-panel]');
      if (panel) panel.style.cssText = '';
      // Clear backdrop
      const backdrop = drawer.querySelector('.veluna-cart-drawer__backdrop, [data-veluna-cart-overlay], [data-cart-drawer-backdrop]');
      if (backdrop) backdrop.style.cssText = '';
    };

    const forceOpenDrawerDOM = () => {
      const drawer = getDrawerEl();
      if (!drawer) return false;

      // Reset ALL inline styles from any close handler (closeDrawerManually, etc.)
      resetDrawerStyles();

      drawer.classList.remove('veluna-cart-drawer--closed');
      drawer.classList.add('veluna-cart-drawer--open');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');

      // Nuclear inline style fallback - guarantees visibility
      drawer.style.setProperty('display', 'block', 'important');
      drawer.style.setProperty('position', 'fixed', 'important');
      drawer.style.setProperty('top', '0', 'important');
      drawer.style.setProperty('right', '0', 'important');
      drawer.style.setProperty('left', 'auto', 'important');
      drawer.style.setProperty('bottom', '0', 'important');
      drawer.style.setProperty('visibility', 'visible', 'important');
      drawer.style.setProperty('opacity', '1', 'important');
      drawer.style.setProperty('pointer-events', 'auto', 'important');
      drawer.style.setProperty('z-index', '999999', 'important');
      const panel = drawer.querySelector('.veluna-cart-drawer__panel');
      if (panel) {
        panel.style.setProperty('display', 'flex', 'important');
        panel.style.setProperty('position', 'fixed', 'important');
        panel.style.setProperty('top', '0', 'important');
        panel.style.setProperty('bottom', '0', 'important');
        panel.style.setProperty('transform', 'translateX(0)', 'important');
        panel.style.setProperty('visibility', 'visible', 'important');
        panel.style.setProperty('right', '0', 'important');
        panel.style.setProperty('pointer-events', 'auto', 'important');
        panel.style.setProperty('z-index', '999999', 'important');
      }
      const backdrop = drawer.querySelector('.veluna-cart-drawer__backdrop, [data-veluna-cart-overlay]');
      if (backdrop) {
        backdrop.style.setProperty('display', 'block', 'important');
        backdrop.style.setProperty('opacity', '1', 'important');
        backdrop.style.setProperty('pointer-events', 'auto', 'important');
      }

      // Ensure arrae-drawer class is on <html>
      document.documentElement.classList.add('veluna-ux--arrae-drawer');

      if (document.body) {
        document.body.classList.add('cart-drawer-open');
        document.body.style.overflow = 'hidden';
      }

      // Refresh cart contents after opening
      if (window.VelunaCartDrawer && typeof window.VelunaCartDrawer.load === 'function') {
        try { window.VelunaCartDrawer.load(); } catch(e) {}
      }

      return isDrawerOpen();
    };

    const openStrategies = [
      // Strategy 1: VelunaCartDrawer.open() - the build JS global API
      () => {
        if (window.VelunaCartDrawer && typeof window.VelunaCartDrawer.open === 'function') {
          resetDrawerStyles(); // Pre-clear styles before calling open
          window.VelunaCartDrawer.open();
          return isDrawerOpen();
        }
        return false;
      },
      // Strategy 2: theme.cartDrawer.openWithRefresh()
      () => {
        if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.openWithRefresh === 'function') {
          resetDrawerStyles();
          window.theme.cartDrawer.openWithRefresh();
          return isDrawerOpen();
        }
        return false;
      },
      // Strategy 3: theme.cartDrawer.open()
      () => {
        if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.open === 'function') {
          resetDrawerStyles();
          window.theme.cartDrawer.open();
          return isDrawerOpen();
        }
        return false;
      },
      // Strategy 4: Legacy global functions
      () => {
        if (typeof window.openVelunaCartDrawer === 'function') {
          resetDrawerStyles();
          window.openVelunaCartDrawer();
          return isDrawerOpen();
        }
        if (typeof window.openCartDrawer === 'function') {
          resetDrawerStyles();
          window.openCartDrawer();
          return isDrawerOpen();
        }
        return false;
      },
      // Strategy 5: Direct DOM open (always works when element exists)
      () => {
        return forceOpenDrawerDOM();
      },
      // Strategy 6: Last-resort click trigger
      () => {
        const trigger = document.querySelector('[data-cart-drawer-open], [data-cart-open], .header__icon--cart, a[href="/cart"], a[href="/cart"] *');
        if (trigger && typeof trigger.click === 'function') {
          trigger.click();
          return isDrawerOpen();
        }
        return false;
      }
    ];

    const runStrategies = () => {
      for (let i = 0; i < openStrategies.length; i++) {
        try {
          const result = openStrategies[i]();
          if (result === true) {
            console.log('[VELUNA SIDE CART] Drawer opened via strategy', i + 1);
            return true;
          }
        } catch (error) {
          // Keep falling back
        }
      }
      return false;
    };

    if (runStrategies()) {
      return;
    }

    // Retries at increasing intervals (for async script loading race conditions)
    setTimeout(runStrategies, 120);
    setTimeout(runStrategies, 300);
    setTimeout(runStrategies, 650);
    // Final nuclear retry: force DOM open regardless
    setTimeout(forceOpenDrawerDOM, 1000);
  }
  
  function handleSideCartAddToCart(button) {
    const sideCart = button.closest('.veluna-product-side-cart');
    if (!sideCart) return;
    
    const productId = button.getAttribute('data-product-id');
    const baseVariantId = getBaseVariantId(sideCart);
    const variantIdRaw = button.getAttribute('data-variant-id') || sideCart.getAttribute('data-variant-id');
    const variantId = variantIdRaw ? parseInt(variantIdRaw, 10) : null;
    const effectiveVariantId = baseVariantId || variantId;
    const purchaseType = sideCart.querySelector('input[name^="purchase-type"]:checked')?.value;
    const schedule = sideCart.querySelector('input[name^="schedule"]:checked')?.value;
    const servings = sideCart.querySelector('input[name^="servings"]:checked')?.value;

    const productHandle = sideCart.getAttribute('data-product-handle');
    const bundleLike = isBundleSetHandle(productHandle);
    
    // Calculate quantity based on purchase type
    let quantity = 1;
    // Commerce semantics (2026-09-09): a subscription always posts the cadence variant
    // (e.g. "3 Month Delivery" / "3 Jars") with THAT variant's cadence selling plan, quantity 1.
    // Multi-month subscriptions are never simulated with base-variant quantity.
    let subscribeVariantId = null;
    let subscribePlanId = null;
    if (purchaseType === 'subscribe' && schedule) {
      if (!bundleLike) {
        const activeOpt = sideCart.querySelector('[data-schedule].active') || sideCart.querySelector(`[data-schedule="${schedule}"]`);
        const optVariant = activeOpt ? parseInt(activeOpt.getAttribute('data-variant-id') || '', 10) : NaN;
        const optPlan = activeOpt ? parseInt(activeOpt.getAttribute('data-selling-plan-id') || '', 10) : NaN;
        if (Number.isFinite(optVariant) && optVariant > 0) subscribeVariantId = optVariant;
        if (Number.isFinite(optPlan) && optPlan > 0) subscribePlanId = optPlan;
      }
    } else if (purchaseType === 'onetime' && servings) {
      quantity = parseInt(servings);
    }
    
    // Store original HTML if not already stored
    if (!button.getAttribute('data-original-html')) {
      button.setAttribute('data-original-html', button.innerHTML);
    }
    const originalHTML = button.getAttribute('data-original-html');
    
    // Disable button during add
    button.disabled = true;
    const priceElement = button.querySelector('[data-side-cart-price]');
    const priceText = priceElement ? priceElement.textContent : '';
    button.innerHTML = '<span class="veluna-product-side-cart__add-to-cart-text">Adding...</span>';
    
    // Build cart payload
    if (!effectiveVariantId || Number.isNaN(effectiveVariantId)) {
      button.disabled = false;
      button.innerHTML = originalHTML;
      updatePrice(sideCart);
      alert('Please select a valid product option and try again.');
      return;
    }

    // Bundle parent products have their own selling plans in Shopify
    // (1/3/6 Month Delivery variants with subscription discounts). We add the
    // bundle parent product directly so the price matches what customers see
    // ($79/$129 base). Quantity = 1 since the cadence variant represents the
    // full delivery (e.g., "6 Month Delivery" = $774 list, $580.50 after 25% off).
    const bundleMeta = getBundleMetaFromSideCart(sideCart);

    if (bundleLike && purchaseType === 'subscribe') {
      const cadenceMonths = schedule ? parseInt(String(schedule), 10) : 0;
      if (!Number.isFinite(cadenceMonths) || cadenceMonths <= 0) {
        button.disabled = false;
        button.innerHTML = originalHTML;
        updatePrice(sideCart);
        alert('Please select a schedule and try again.');
        return;
      }

      // Read variant ID directly from the active schedule option element.
      // Each schedule label in the Liquid template has data-variant-id baked
      // in (v_6.id / v_3.id / v_1.id).  This is reliable regardless of
      // whether syncVariantFromScheduleOption has run yet.
      var activeScheduleOpt = sideCart.querySelector('[data-schedule].active');
      var cadenceVariantId = activeScheduleOpt
        ? parseInt(activeScheduleOpt.getAttribute('data-variant-id'), 10)
        : (variantId || effectiveVariantId);

      if (!cadenceVariantId || Number.isNaN(cadenceVariantId)) {
        cadenceVariantId = effectiveVariantId;
      }

      // Shared display info used by both parent-add and component-fallback paths
      var bundleDisplayTitle = (sideCart.getAttribute('data-product-title')
        || (sideCart.querySelector('.veluna-product-side-cart__product-title')
            && sideCart.querySelector('.veluna-product-side-cart__product-title').textContent)
        || '').toString().trim();
      var bundleGroupKey = (productHandle || 'bundle') + '::' + String(cadenceMonths) + '::subscribe';

      // - Component-fallback helper (safety net) -
      // If the bundle parent variant ever gets a 422 from Shopify,
      // decompose into individual component products with their own selling plans.
      function addBundleViaComponents(parentProduct) {
        var compHandles = getComponentHandlesForBundle(bundleMeta.ritualType, bundleMeta.ritualTier);
        if (!compHandles || !compHandles.length) {
          throw new Error('No component mapping for ' + bundleMeta.ritualType + ' / ' + bundleMeta.ritualTier);
        }

        var bundleParentImage = (sideCart.getAttribute('data-product-image') || '').toString().trim()
          || (parentProduct.featured_image
            || (Array.isArray(parentProduct.images) && parentProduct.images.length ? parentProduct.images[0] : '')
            || '');

        return Promise.all(compHandles.map(function (h) { return fetchProductJsonByHandle(h); }))
          .then(function (compProducts) {
            var items = [];
            for (var i = 0; i < compProducts.length; i++) {
              var cp = compProducts[i];
              if (!cp) throw new Error('Failed to load component: ' + compHandles[i]);
              var compVariantId = findBaseVariantIdFromProductJson(cp);
              if (!compVariantId) throw new Error('No variant for component: ' + compHandles[i]);
              var compPlanId = findSellingPlanIdForCadence(cp, compVariantId, String(cadenceMonths));
              var item = {
                id: parseInt(String(compVariantId), 10),
                quantity: 1,
                properties: {
                  '_purchase_type': 'subscribe',
                  '_subscription_schedule': String(cadenceMonths),
                  '_bundle_parent_handle': productHandle || '',
                  '_bundle_parent_title': bundleDisplayTitle || (productHandle || ''),
                  '_bundle_parent_image': bundleParentImage || '',
                  '_bundle_group_key': bundleGroupKey,
                  '_bundle_ritual_type': bundleMeta.ritualType || '',
                  '_bundle_ritual_tier': bundleMeta.ritualTier || ''
                }
              };
              if (compPlanId) item.selling_plan = parseInt(String(compPlanId), 10);
              items.push(item);
            }
            return addItemsToCart({ items: items });
          });
      }

      fetchProductJsonByHandle(productHandle)
        .then(function (parentProduct) {
          if (!parentProduct) throw new Error('Failed to load product: ' + productHandle);

          var planId = findSellingPlanIdForCadence(parentProduct, cadenceVariantId, String(cadenceMonths));
          if (!planId) throw new Error('Missing selling plan for cadence ' + cadenceMonths);

          var bundleParentImage = (sideCart.getAttribute('data-product-image') || '').toString().trim()
            || (parentProduct.featured_image
              || (Array.isArray(parentProduct.images) && parentProduct.images.length ? parentProduct.images[0] : '')
              || '');

          return addItemsToCart({
            items: [{
              id: parseInt(String(cadenceVariantId), 10),
              quantity: 1,
              selling_plan: parseInt(String(planId), 10),
              properties: {
                '_purchase_type': 'subscribe',
                '_subscription_schedule': String(cadenceMonths),
                '_bundle_parent_handle': productHandle || '',
                '_bundle_parent_title': bundleDisplayTitle || (productHandle || ''),
                '_bundle_parent_image': bundleParentImage || '',
                '_bundle_group_key': bundleGroupKey,
                '_bundle_ritual_type': bundleMeta.ritualType || '',
                '_bundle_ritual_tier': bundleMeta.ritualTier || ''
              }
            }]
          }).catch(function (err) {
            // Safety net: if Shopify returns 422 for any variant,
            // fall back to adding individual component products.
            if (err && err.status === 422) {
              console.warn('[VELUNA SIDE CART] Bundle parent add failed (422), falling back to components', productHandle, cadenceMonths);
              return addBundleViaComponents(parentProduct);
            }
            throw err;
          });
        })
        .then(function () {
          button.disabled = false;
          button.innerHTML = originalHTML;
          closeSideCart();
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              openCartDrawerReliably();
            });
          });
          document.dispatchEvent(new CustomEvent('veluna:cart:added', {
            detail: { productId: productId, variantId: cadenceVariantId, purchaseType: purchaseType, schedule: schedule, quantity: 1, bundle: true }
          }));
        })
        .catch(function (error) {
          var message = getReadableErrorMessage(error) || 'There was an error adding the bundle subscription. Please try again.';
          console.error('[VELUNA SIDE CART] Error adding bundle subscription:', {
            error: error, message: message, productId: productId,
            productHandle: productHandle, bundleMeta: bundleMeta, schedule: schedule
          });
          button.disabled = false;
          button.innerHTML = originalHTML;
          updatePrice(sideCart);
          alert(message);
        });

      return;
    }

    const itemVariantId = (purchaseType === 'subscribe' && subscribeVariantId) ? subscribeVariantId : effectiveVariantId;
    const baseItem = {
      id: itemVariantId,
      quantity: quantity,
      properties: {
        '_purchase_type': purchaseType,
        // Only a subscription has a cadence; one-time lines must not carry the stale schedule radio value.
        '_subscription_schedule': purchaseType === 'subscribe' ? (schedule || '') : '',
        '_servings': purchaseType === 'onetime' ? (servings || '') : ''
      }
    };

    // Resolve selling_plan for subscription purchases (needed for products that require a plan).
    const sellingPlanPromise = (purchaseType === 'subscribe')
      ? fetchProductJsonByHandle(productHandle).then((product) => {
          // Prefer the plan the option was rendered with; verify it is allocated to the variant.
          const variantJson = findVariantInProductJson(product, itemVariantId);
          const allocated = variantJson && Array.isArray(variantJson.selling_plan_allocations)
            ? variantJson.selling_plan_allocations.some((a) => a && String(a.selling_plan_id) === String(subscribePlanId))
            : false;
          if (subscribePlanId && allocated) return subscribePlanId;
          const planId = findSellingPlanIdForCadence(product, itemVariantId, schedule || '');
          if (!planId) {
            const err = new Error('This delivery schedule is not available for this product.');
            err.status = 422;
            throw err;
          }
          return parseInt(planId, 10);
        })
      : Promise.resolve(null);

    sellingPlanPromise
      .then((sellingPlanId) => {
        const item = Object.assign({}, baseItem);
        if (sellingPlanId) {
          item.selling_plan = sellingPlanId;
        }
        return addItemsToCart({ items: [item] });
      })
      .then(() => {

      // CRITICAL: Reset button state before closing
      button.disabled = false;
      button.innerHTML = originalHTML;
      
      // Success - close side cart first
      closeSideCart();
      
      // Use requestAnimationFrame to ensure the browser has painted the side cart
      // close before we try to open the cart drawer. This prevents style conflicts
      // between the two overlays.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          openCartDrawerReliably();
        });
      });
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('veluna:cart:added', {
        detail: { productId, variantId: itemVariantId, purchaseType, schedule, quantity }
      }));
    })
    .catch((error) => {
      const message = getReadableErrorMessage(error) || 'There was an error adding the product to your cart. Please try again.';
      console.error('[VELUNA SIDE CART] Error adding to cart:', {
        error,
        message,
        productId,
        variantId,
        purchaseType,
        schedule,
        servings,
        productHandle
      });
      // Reset button state on error
      button.disabled = false;
      button.innerHTML = originalHTML;
      updatePrice(sideCart);
      
      // Show error message
      alert(message);
    });
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductSideCart);
  } else {
    initProductSideCart();
  }
  
  // Re-initialize on dynamic content changes
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      const hasNewButtons = Array.from(mutations).some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && node.querySelector && node.querySelector('[data-open-side-cart]')
        )
      );
      
      if (hasNewButtons) {
        initProductSideCart();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
})();
